import React, { useEffect, useRef, useState } from 'react';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ActionButton, Text, Flex, ProgressCircle, TextField, View, Content, Link } from '@adobe/react-spectrum';
import { DropDownTreeComponent } from '@syncfusion/ej2-react-dropdowns';
import { ToastQueue } from '@adobe/react-spectrum';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import TinyMCEEditor from '../../../ui/TinyMCEEditor/TinyMCEEditior';
import WindowComponent from '../../../ui/WindowComponent';
import { CreateDocumentRequest } from '../../../../services/soap/document_library/requests/CreateDocumentRequest';
import { SaveDocumentRequest } from '../../../../services/soap/document_library/requests/SaveDocumentRequest';
import useAutoSave from '../../../../hooks/useAutoSave';
import CustomDialogComponent from '../../../ui/CustomDialogComponent';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { AlertIconFill, NewMessageIcon } from '../../Icons/IconsLib';
import { truncateText } from '../../../../utils/helperUtil';
import styles from './DocumentComponent.module.css';

interface DocumentCreateUpdateProps {
  categoryId: any;
  documentCategoryData: any;
  close: () => void;
  selectedItem?: FPAData;
  getDocumentsData: () => void;

  docData?: any;

  setIsCreateOpen: any;

  isCreateOpen: boolean;
}

const DocumentCreateUpdate: React.FC<DocumentCreateUpdateProps> = ({ categoryId, documentCategoryData, close, selectedItem, getDocumentsData, docData, setIsCreateOpen, isCreateOpen }) => {
  const { isMobile } = useViewInfo();
  const { documentLibraryService, store } = useDependency();
  const { t } = useTranslation();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [categoryItem] = useState<any>([categoryId || '']);
  const [newDocumentFileds, setNewDocumentFileds] = useState<any>({
    category: '',
    title: '',
    documentContent: '',
  });
  const documentFileds = useRef<any>({
    category: '',
    title: '',
    documentContent: '',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmButtonShow, setConfirmButtonShow] = useState(false);
  const [docDataRef, setDocDataRef] = useState(docData || null);
  const [isDocumentEdited, setIsDocumentEdited] = useState(false);
  const [visibility, setVisibility] = useState<boolean>(isCreateOpen);
  useAutoSave(hasUnsavedChanges, 5 * 60 * 1000, autoSaveDocument);
  let fields = { dataSource: documentCategoryData, value: 'id', text: 'name', child: 'subChild' };

  useEffect(() => {
    setVisibility(isCreateOpen);
    if (docData) {
      setDocDataRef(docData);
      documentFileds.current = {
        category: docData.category || categoryId,
        title: docData.name,
        documentContent: docData.documentContent,
      };
      setNewDocumentFileds({
        category: docData.category || categoryId,
        title: docData.name,
        documentContent: docData.documentContent,
      });
    } else {
      setDocDataRef(null);
      documentFileds.current = { category: '', title: '', documentContent: '' };
      setNewDocumentFileds({ category: '', title: '', documentContent: '' });
    }
  }, [isCreateOpen]);

  useEffect(() => {
    if (docData) {
      setIsDocumentEdited(true);
      //  console.log('useEffect docData', docData);
      setNewDocumentFileds({
        category: docData.category || categoryId,
        title: docData.name,
        documentContent: docData.documentContent,
      });
    }
  }, [docData]);

  const getCompleteHtml = () => {
    const content = newDocumentFileds.documentContent;
    // Create a complete HTML document
    const completeHtml = `<!DOCTYPE html>
      <html>
        <body style="font-family: 'Adobe Clean', sans-serif;">
          ${content}
        </body>
      </html>`;

    return completeHtml;
  };

  function convertHtmlToBase64(htmlContent: string): string {
    try {
      if (typeof window !== 'undefined') {
        return btoa(unescape(encodeURIComponent(htmlContent)));
      }
      return '';
    } catch (error) {
      console.error('Error converting HTML to base64:', error);
      return ''; // Return empty string on error
    }
  }

  const sanitizeFileName = (filename: string): string => {
    return filename
      .replace(/[^a-zA-Z0-9]/g, '_') // Remove all non-alphanumeric characters
      .trim();
  };

  const saveDocument = async () => {
    try {
      if (!newDocumentFileds.title) {
        ToastQueue.negative(
          t('please_enter_the_title', {
            ns: 'layout_components',
            defaultValue: 'Please enter the title',
          }),
          { timeout: 100 }
        );
        return;
      }

      const documentContent = convertHtmlToBase64(getCompleteHtml());
      //console.log('sanitizedTitle', sanitizedTitle);
      const documentParams = {
        name: newDocumentFileds.title || 'document',
        category: newDocumentFileds.category || categoryId,
        treeHandle: String(selectedItem?.id),
        contextId: String(selectedItem?.id),
        fileName: `${sanitizeFileName(newDocumentFileds.title) || 'document'}.html`,
        parent: '',
        mimeContentType: 'text/html',
      };

      setShowLoader(true);

      const request = docData
        ? documentLibraryService.saveDocument(new SaveDocumentRequest(store.Server, store.SessionId, { ...documentParams, parent: docData.id }, documentContent))
        : documentLibraryService.createDocument(new CreateDocumentRequest(store.Server, store.SessionId, documentParams, documentContent));

      const response = await request;
      if (response.result === 'OK') {
        ToastQueue.positive(
          docData
            ? t('document_updated_successfully', {
                ns: 'layout_components',
                defaultValue: 'Document updated successfully',
              })
            : t('document_created_successfully', {
                ns: 'layout_components',
                defaultValue: 'Document created successfully',
              }),
          { timeout: 100 }
        );
        close();
        getDocumentsData();
      } else {
        ToastQueue.negative(response.EXCEPTION.message, { timeout: 100 });
      }
    } finally {
      setShowLoader(false);
    }
  };

  async function autoSaveDocument() {
    try {
      //console.log('AutoSaved called');
      const documentContent = btoa(getCompleteHtml());
      const documentParams = {
        name: newDocumentFileds.title || 'AutoSaved',
        category: newDocumentFileds.category || categoryId,
        treeHandle: String(selectedItem?.id),
        contextId: String(selectedItem?.id),
        fileName: `${sanitizeFileName(newDocumentFileds.title) || 'document'}.html`,
        parent: '',
        mimeContentType: 'text/html',
      };
      //console.log('Before Saving docData', docDataRef);
      const request = docDataRef
        ? documentLibraryService.saveDocument(new SaveDocumentRequest(store.Server, store.SessionId, { ...documentParams, parent: docDataRef.id }, documentContent))
        : documentLibraryService.createDocument(new CreateDocumentRequest(store.Server, store.SessionId, documentParams, documentContent));

      const response: any = await request;
      if (response.result === 'OK') {
        setDocDataRef({ ...documentParams, id: response.newId });
        //console.log('After Saving docData', docDataRef);
        ToastQueue.positive(
          t('document_auto_saved_successfully', {
            ns: 'layout_components',
            defaultValue: 'Document auto saved successfully',
          }),
          { timeout: 100 }
        );
        getDocumentsData();
      } else {
        ToastQueue.negative(response.EXCEPTION.message, { timeout: 100 });
      }
    } finally {
      setHasUnsavedChanges(false);
    }
  }

  const onCategoryChange = (args: any): void => {
    //console.log('args', args.value[0]);
    setNewDocumentFileds({ ...newDocumentFileds, category: args.value[0] });
  };

  function newMessageDialogheader(): JSX.Element {
    if (isMobile) {
      return (
        <Flex direction={'row'} justifyContent={'space-between'} alignItems={'start'}>
          <Flex direction={'row'} UNSAFE_className={styles.new_document_heading} gap={'10px'}>
            {docData ? t('edit_document', { ns: 'layout_components' }) : t('add_document', { ns: 'layout_components' })} <i className="bi bi-file-earmark-plus-fill" style={{ color: '#B0BEC5' }}></i>
          </Flex>
        </Flex>
      );
    } else
      return (
        <Flex direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <Flex direction={'row'} UNSAFE_className={styles.new_document_heading} gap={'10px'}>
            {docData ? t('edit_document', { ns: 'layout_components' }) : t('add_document', { ns: 'layout_components' })} <i className="bi bi-file-earmark-plus-fill"></i>
          </Flex>
          <Flex direction={'row'} UNSAFE_className={styles.label_message_type} gap={'10px'} alignContent={'center'} alignItems={'center'}>
            {t('category', { ns: 'layout_components' })}:
            <View marginEnd={'10px'}>
              <DropDownTreeComponent value={categoryItem} fields={fields} change={onCategoryChange} changeOnBlur={false} placeholder="" popupHeight="200px" width={'215px'} />
            </View>
          </Flex>
        </Flex>
      );
  }
  function newMessageDialogContent(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={'center'} marginTop={10}>
            <ProgressCircle aria-label="Loading…" isIndeterminate />
          </Flex>
        );
      } else
        return (
          <View UNSAFE_className={styles.document_modal_mobile}>
            <Flex direction={'column'} width={'100%'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.document_options}>
              <Flex direction={'row'} alignSelf={'stretch'} position={'relative'} flexShrink={0}>
                <View UNSAFE_className={styles.label_wrapper_mobile} width={'15%'}>
                  <b>{t('category', { ns: 'layout_components' })} :</b>
                </View>
                <View UNSAFE_className={styles.label_wrapper_mobile} width={'85%'}>
                  <DropDownTreeComponent value={categoryItem} fields={fields} change={onCategoryChange} changeOnBlur={false} placeholder="" popupHeight="200px" width={'100%'} />
                </View>
              </Flex>
              <Flex direction={'row'} alignSelf={'stretch'} position={'relative'} flexShrink={0}>
                <View UNSAFE_className={styles.label_wrapper_mobile} width={'12%'}>
                  <b>{t('title', { ns: 'layout_components' })} :</b>
                </View>
                <View UNSAFE_className={styles.label_wrapper_mobile} width={'88%'}>
                  <TextField
                    id="txtTitle"
                    defaultValue={newDocumentFileds.title}
                    value={newDocumentFileds.title}
                    width={'100%'}
                    onChange={e => setNewDocumentFileds({ ...newDocumentFileds, title: e })}
                    UNSAFE_style={{
                      backgroundColor: 'transparent !important',
                      padding: '5px',
                      color: 'black',
                    }}
                  />
                </View>
              </Flex>

              <Flex direction={'column'} position={'relative'} marginTop={'10px'} width={'100vw'} minHeight={'320px'} alignItems={'start'} justifyContent={'start'} zIndex={0}>
                <TinyMCEEditor
                  initialValue={newDocumentFileds.documentContent}
                  onChange={(e: string) => {
                    // Only update state if content is actually different
                    if (e !== newDocumentFileds.documentContent) {
                      // Handle special case first
                      if (isDocumentEdited) {
                        setIsDocumentEdited(false);
                        // No need to set hasUnsavedChanges as we're resetting the edit flag
                      } else {
                        setHasUnsavedChanges(true);
                      }
                      // Single state update for better performance
                      setNewDocumentFileds((prev: any) => ({ ...prev, documentContent: e }));
                    }
                  }}
                  height={window.innerHeight - 300}
                />
              </Flex>
            </Flex>
          </View>
        );
    } else if (showLoader) {
      return (
        <Flex width="100%" justifyContent={'center'} marginTop={10}>
          <ProgressCircle aria-label="Loading…" isIndeterminate />
        </Flex>
      );
    } else {
      return (
        <View UNSAFE_className={styles.document_modal}>
          <Flex direction={'column'} width={'100%'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.document_options}>
            <Flex direction={'row'} top={'14px'} alignSelf={'stretch'} position={'relative'} flexShrink={0} gap={'size-200'}>
              <View position={'relative'} width={'5%'} left={'8px'} alignSelf={'center'}>
                <b>{t('title', { ns: 'layout_components' })}:</b>
              </View>
              <View width={'90%'} marginStart={'5px'} UNSAFE_className={styles.label_wrapper}>
                <TextField
                  id="txtTitle"
                  defaultValue={newDocumentFileds.title}
                  value={newDocumentFileds.title}
                  width={'100%'}
                  height={'25px'}
                  onChange={e => setNewDocumentFileds({ ...newDocumentFileds, title: e })}
                />
              </View>
            </Flex>
          </Flex>

          <Flex
            direction={'column'}
            position={'relative'}
            marginTop={'20px'}
            width={'100%'}
            height={'90%'}
            minHeight={'420px'}
            alignItems={'start'}
            justifyContent={'start'}
            zIndex={9999999}
            UNSAFE_className={styles.document_options}
          >
            <TinyMCEEditor
              initialValue={newDocumentFileds.documentContent}
              onChange={(e: string) => {
                // Only update state if content is actually different
                if (e !== newDocumentFileds.documentContent) {
                  // Handle special case first
                  if (isDocumentEdited) {
                    setIsDocumentEdited(false);
                    // No need to set hasUnsavedChanges as we're resetting the edit flag
                  } else {
                    setHasUnsavedChanges(true);
                  }
                  // Single state update for better performance
                  setNewDocumentFileds((prev: any) => ({ ...prev, documentContent: e }));
                }
              }}
              height={`${window.innerHeight - 230}px`}
            />
          </Flex>
        </View>
      );
    }
  }

  const MessageDialog = ({ close }: { close: () => void }) => (
    <Flex UNSAFE_className={styles.delete_pop_up_parent}>
      <Content UNSAFE_className={styles.delete_pop_up_header}>{t('close_document', { ns: 'layout_components' })}</Content>
      <View UNSAFE_className={styles.delete_pop_up_line}></View>
      <View UNSAFE_className={styles.delete_pop_up_warning_text}>{t('do_you_want_to_close_without_saving', { ns: 'layout_components' })}</View>
      <View UNSAFE_className={styles.buttons_parent}>
        <View UNSAFE_className={styles.button_cancel}>
          <Link
            UNSAFE_className={styles.delete_pop_up_cancel_text}
            isQuiet
            onPress={() => {
              setConfirmButtonShow(false);
            }}
          >
            {t('no', { ns: 'layout_components' })}
          </Link>
        </View>
        <View UNSAFE_className={styles.button_confirm}>
          <div className={styles.trash_icon}>{/* <TrashIcon size={32} /> */}</div>
          <Link UNSAFE_className={styles.delete_pop_up_confirm_text} isQuiet onPress={() => close()}>
            {t('close_document', { ns: 'layout_components' })}
          </Link>
        </View>
      </View>
      <View UNSAFE_className={styles.ellipse_div}>
        <View UNSAFE_className={styles.delete_alert_icon}>
          <AlertIconFill size={72} />
        </View>
      </View>
    </Flex>
  );

  function newMessageDialogFooter(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={'center'} marginTop={10}>
            <ProgressCircle aria-label="Loading…" isIndeterminate />
          </Flex>
        );
      } else
        return (
          <Flex gap={'10px'} direction={'column'} alignItems={'start'} width={'100%'}>
            <Flex direction={'row'} alignItems={'start'} width={'100%'}>
              {confirmButtonShow && (
                <Dialog width={'100%'} height={'400px'} title={t('close_document', { ns: 'layout_components' })} onClose={close}>
                  <View>{<MessageDialog close={close} />}</View>
                </Dialog>
              )}
            </Flex>
            <Flex gap={'8px'} alignItems={'center'}>
              <ActionButton
                onPress={async e => {
                  await saveDocument();
                }}
                UNSAFE_className={`${styles.action_button} ${styles.save_document_button}`}
              >
                <Text UNSAFE_className={styles.btn_save_document}>{t('save', { ns: 'layout_components' })}</Text>
              </ActionButton>
            </Flex>
          </Flex>
        );
    }

    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={'center'} marginTop={10}>
          <ProgressCircle aria-label="Loading…" isIndeterminate />
        </Flex>
      );
    } else
      return (
        <Flex alignItems={'center'} width={'100%'} justifyContent={'space-between'} gap={'10px'}>
          <Flex direction={'row'} alignItems={'start'} width={'80%'}>
            {confirmButtonShow && (
              <Dialog width={'410px'} height={'400px'} title={t('close_document', { ns: 'layout_components' })} onClose={close}>
                <View>{<MessageDialog close={close} />}</View>
              </Dialog>
            )}
          </Flex>

          <Flex gap={'8px'} alignItems={'center'} justifyContent={'end'} width={'20%'}>
            {!showLoader && (
              <>
                <ActionButton
                  onPress={async (e: any) => {
                    await saveDocument();
                  }}
                  UNSAFE_className={`${styles.action_button} ${styles.save_document_button}`}
                >
                  <Text UNSAFE_className={styles.btn_save_document}>{t('save', { ns: 'layout_components' })}</Text>
                </ActionButton>
              </>
            )}
          </Flex>
        </Flex>
      );
  }

  const closeDialog = () => {
    setVisibility(false);
    setIsCreateOpen(false);
  };

  const popupClose = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      close();
    }
  };

  const handleClose = () => {
    //console.log('hasUnsavedChanges', hasUnsavedChanges);
    if (hasUnsavedChanges && newDocumentFileds.documentContent.trim() !== '') {
      setConfirmButtonShow(true);
      return false;
    } else {
      closeDialog();
    }
  };

  function minimizedDialogheader(): JSX.Element {
    return (
      <Flex direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Flex direction={'row'} UNSAFE_className={styles.message_heading_small} gap={'10px'}>
          {newDocumentFileds.title ? truncateText(newDocumentFileds.title, 15) : docData ? t('edit_document', { ns: 'layout_components' }) : t('add_document', { ns: 'layout_components' })}{' '}
          <i className="bi bi-file-earmark-plus-fill" style={{ color: '#B0BEC5' }}></i>
        </Flex>
      </Flex>
    );
  }

  return showLoader ? (
    <Flex width="100%" justifyContent={'center'} marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  ) : (
    visibility && (
      <Dialog title={newMessageDialogheader()} onClose={handleClose} style={{ zIndex: 100 }} modal={false}>
        <Flex direction={'column'} width={'100%'} height={'100%'} UNSAFE_className={styles.document_dialog}>
          <View>{newMessageDialogContent()}</View>
        </Flex>
        <DialogActionsBar>
          <View>{newMessageDialogFooter()}</View>
        </DialogActionsBar>
      </Dialog>
    )
    // visibility && (
    //   <div onKeyUp={popupClose}>
    //     <WindowComponent
    //       id={`new-message-window_1`}
    //       windowIndex={1}
    //       visible={visibility}
    //       title={newMessageDialogheader()}
    //       minimizedTitle={minimizedDialogheader()}
    //       content={newMessageDialogContent()}
    //       footer={newMessageDialogFooter()}
    //       width={isMobile ? window.innerWidth : window.innerWidth - 300}
    //       height={isMobile ? window.innerHeight : 700}
    //       draggable={!isMobile}
    //       resizable={!isMobile}
    //       zIndex={1000}
    //       onClose={() => closeDialog()}
    //     />
    //   </div>
    // )
  );
};

export default DocumentCreateUpdate;
